import React, { useEffect, useState } from "react";
import { Megaphone, Pencil, Plus, Trash2 } from "lucide-react";
import api from '../../api/axios';
import DateFilter from "../../components/dashboard/DateRangeFilter";
import { useToast } from "../../context/ToastContext";

export default function CampaignComponent({ role }) {
    const [campaigns, setCampaigns] = useState([]);
    const [formData, setFormData] = useState({
        camp_name: "",
        camp_start_date: "",
        camp_end_date: "",
        camp_status: "ACTIVE"
    });
    const [errors, setErrors] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);

    const [deleteId, setDeleteId] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [search, setSearch] = useState("");

    const { success, error } = useToast();

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            setErrors(validate());
        }
    }, [formData]);

    // ===========================
    // FETCH CAMPAIGNS
    // ===========================
    const fetchCampaigns = async () => {
        try {
            const response = await api.get("/campaign/list");
            if (response?.data?.data) {
                setCampaigns(response.data.data);
            }
        } catch (error) {
            console.error("Fetch error:", error);
        }
    };

    useEffect(() => {
        fetchCampaigns();
    }, []);

    const filteredCampaigns = campaigns.filter((campaign) =>
        campaign.camp_name
            ?.toLowerCase()
            .includes(search.toLowerCase())
    );

    // ===========================
    // HANDLE CHANGE
    // ===========================
    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleDateChange = (key, e) => {
        setFormData({
            ...formData,
            [key]: e,
        });

    };

    // ===========================
    // VALIDATION
    // ===========================
    const validate = () => {
        let newErrors = {};

        if (!formData.camp_name.trim())
            newErrors.camp_name = "Campaign name is required";

        if (!formData.camp_start_date)
            newErrors.camp_start_date = "Start date is required";

        if (!formData.camp_end_date)
            newErrors.camp_end_date = "End date is required";

        if (!formData.camp_status)
            newErrors.camp_status = "Status is required";

        // ✅ FIXED DATE VALIDATION
        if (formData.camp_start_date && formData.camp_end_date) {
            const start = new Date(formData.camp_start_date);
            const end = new Date(formData.camp_end_date);

            if (end < start) {
                newErrors.camp_end_date = "End date must be after start date";
            }
        }
        return newErrors;
    };

    // ===========================
    // SUBMIT (CREATE / UPDATE)
    // ===========================
    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length !== 0) return;

        try {
            setLoading(true);

            const payload = {
                ...formData,
                camp_start_date: formData.camp_start_date
                    ? new Date(formData.camp_start_date).toISOString()
                    : null,
                camp_end_date: formData.camp_end_date
                    ? new Date(formData.camp_end_date).toISOString()
                    : null,
            };

            if (editingId) {
                await api.put(`/campaign/${editingId}`, payload);
                success("Campaign updated successfully");
            } else {
                await api.post(`/campaign/save`, payload);
                success("Campaign added successfully");
            }

            setFormData({
                camp_name: "",
                camp_start_date: "",
                camp_end_date: "",
                camp_status: "ACTIVE"
            });

            setEditingId(null);
            setOpenModal(false);
            fetchCampaigns();
        } catch (err) {
            if (err.response?.status === 409) {
                error(err.response.data.error);
            } else {
                error(err?.data?.message || 'failed to create campaign');
            }
        } finally {
            setLoading(false);
        }
    };

    // ===========================
    // EDIT
    // ===========================
    const handleEdit = (campaign) => {
        setFormData({
            camp_name: campaign.camp_name,
            camp_start_date: campaign.camp_start_date,
            camp_end_date: campaign.camp_end_date,
            camp_status: campaign.camp_status,
        });

        setEditingId(campaign.camp_id);
        setOpenModal(true);
    };

    // ===========================
    // DELETE
    // ===========================
    const handleDelete = async () => {
        if (!deleteId) return;

        try {
            setDeleteLoading(true);
            await api.delete(`/campaign/${deleteId}`);
            fetchCampaigns();
            setDeleteId(null);
            success("Campaign deleted successfully");
        } catch (error) {
            console.error("Delete error:", error);
        } finally {
            setDeleteLoading(false);
        }
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="p-6">

            <div className="flex items-center justify-between">
                <div>
                    <h1 className="premium-page-title">Campaigns</h1>
                    <p className="text-muted-foreground">Manage all your campaigns</p>
                </div>


                <button
                    className="dashboard-btn-new hover:-translate-y-px transition-transform duration-150 ease-in-out"
                    onClick={() => {
                        setEditingId(null);

                        setFormData({
                            camp_name: "",
                            camp_start_date: "",
                            camp_end_date: "",
                            camp_status: "ACTIVE",
                        });

                        setErrors({});
                        setOpenModal(true);
                    }}
                >
                    <span className="dashboard-btn-plus">
                        <Plus className="h-4 w-4" />
                    </span> Create Campaign
                </button>
            </div>

            <div className="mx-auto grid md:grid-cols-1 gap-6 mt-5">

                {/* ================= FORM ================= */}
                {openModal && (
                    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">

                        <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl overflow-hidden">

                            {/* Header */}

                            <div className="flex items-center justify-between border-b px-6 py-5">

                                <div className="flex items-center gap-3">

                                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">
                                        <Megaphone className="h-6 w-6 text-blue-600" />
                                    </div>

                                    <div>
                                        <h2 className="text-xl font-bold text-gray-900">
                                            {editingId ? "Update Campaign" : "Create Campaign"}
                                        </h2>

                                        <p className="text-sm text-gray-500">
                                            Fill in the campaign details.
                                        </p>
                                    </div>

                                </div>

                                <button
                                    onClick={() => {
                                        setOpenModal(false);
                                        setEditingId(null);

                                        setFormData({
                                            camp_name: "",
                                            camp_start_date: "",
                                            camp_end_date: "",
                                            camp_status: "ACTIVE",
                                        });

                                        setErrors({});
                                    }}
                                    className="text-3xl leading-none text-gray-400 hover:text-red-500"
                                >
                                    ×
                                </button>

                            </div>

                            {/* Body */}

                            <form onSubmit={handleSubmit}>

                                <div className="space-y-6 p-6">

                                    <div>

                                        <label className="mb-2 block text-sm font-medium text-gray-700">
                                            Campaign Name
                                        </label>

                                        <input
                                            type="text"
                                            name="camp_name"
                                            placeholder="Enter campaign name"
                                            value={formData.camp_name}
                                            onChange={handleChange}
                                            className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-blue-500 focus:outline-none"
                                        />

                                        {errors.camp_name && (
                                            <p className="mt-1 text-xs text-red-500">
                                                {errors.camp_name}
                                            </p>
                                        )}

                                    </div>

                                    <div className="grid gap-5 md:grid-cols-2">

                                        <div>

                                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                                Start Date
                                            </label>

                                            <DateFilter
                                                className="w-full"
                                                name="camp_start_date"
                                                value={formData.camp_start_date}
                                                onDateChange={(date) =>
                                                    handleDateChange("camp_start_date", date)
                                                }
                                            />

                                            {errors.camp_start_date && (
                                                <p className="mt-1 text-xs text-red-500">
                                                    {errors.camp_start_date}
                                                </p>
                                            )}

                                        </div>

                                        <div>

                                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                                End Date
                                            </label>

                                            <DateFilter
                                                className="w-full"
                                                name="camp_end_date"
                                                value={formData.camp_end_date}
                                                onDateChange={(date) =>
                                                    handleDateChange("camp_end_date", date)
                                                }
                                            />

                                            {errors.camp_end_date && (
                                                <p className="mt-1 text-xs text-red-500">
                                                    {errors.camp_end_date}
                                                </p>
                                            )}

                                        </div>

                                    </div>

                                    <div>

                                        <label className="mb-2 block text-sm font-medium text-gray-700">
                                            Status
                                        </label>

                                        <select
                                            name="camp_status"
                                            value={formData.camp_status}
                                            onChange={handleChange}
                                            className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:border-blue-500 focus:outline-none"
                                        >
                                            <option value="ACTIVE">Active</option>
                                            <option value="INACTIVE">Inactive</option>
                                        </select>

                                        {errors.camp_status && (
                                            <p className="mt-1 text-xs text-red-500">
                                                {errors.camp_status}
                                            </p>
                                        )}

                                    </div>

                                </div>

                                {/* Footer */}

                                <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">

                                    <button
                                        type="button"
                                        onClick={() => {
                                            setOpenModal(false);
                                            setEditingId(null);
                                        }}
                                        className="rounded-xl border border-gray-300 px-5 py-2.5 hover:bg-gray-100"
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2.5 text-white hover:bg-blue-700 disabled:opacity-60"
                                    >
                                        {loading ? (
                                            <>
                                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
                                                Saving...
                                            </>
                                        ) : (
                                            editingId ? "Update Campaign" : "Create Campaign"
                                        )}
                                    </button>

                                </div>

                            </form>

                        </div>

                    </div>
                )}

                {/* ================= LIST ================= */}
                <div className="rounded-xl bg-white shadow">

                    {/* Header */}

                    <div className="flex flex-col gap-4 border-b p-6 md:flex-row md:items-center md:justify-between">

                        <input
                            type="text"
                            name="campaign_search"
                            placeholder="Search campaign..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-blue-500 focus:outline-none"
                        />

                    </div>

                    {/* Table */}

                    <div className="overflow-x-auto">

                        <table className="min-w-full">

                            <thead className="bg-gray-50">

                                <tr>

                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                                        Campaign
                                    </th>

                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                                        Start Date
                                    </th>

                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">
                                        End Date
                                    </th>

                                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                                        Status
                                    </th>

                                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">
                                        Actions
                                    </th>

                                </tr>

                            </thead>

                            <tbody>

                                {filteredCampaigns.length > 0 ? (

                                    filteredCampaigns.map((campaign) => (

                                        <tr
                                            key={campaign.camp_id}
                                            className="border-t hover:bg-gray-50 transition"
                                        >

                                            <td className="px-6 py-5">

                                                <div className="flex items-center gap-4">

                                                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100">

                                                        <Megaphone
                                                            size={22}
                                                            className="text-blue-600"
                                                        />

                                                    </div>

                                                    <div>

                                                        <h4 className="font-semibold text-gray-800">
                                                            {campaign.camp_name}
                                                        </h4>

                                                        <p className="text-xs text-gray-500">
                                                            Campaign
                                                        </p>

                                                    </div>

                                                </div>

                                            </td>

                                            <td className="px-6 py-5 text-sm text-gray-600">
                                                {new Date(
                                                    campaign.camp_start_date
                                                ).toLocaleDateString()}
                                            </td>

                                            <td className="px-6 py-5 text-sm text-gray-600">
                                                {new Date(
                                                    campaign.camp_end_date
                                                ).toLocaleDateString()}
                                            </td>

                                            <td className="px-6 py-5 text-center">

                                                <span
                                                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${campaign.camp_status === "ACTIVE"
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-red-100 text-red-700"
                                                        }`}
                                                >
                                                    {campaign.camp_status}
                                                </span>

                                            </td>

                                            <td className="px-6 py-5">

                                                <div className="flex justify-center gap-3">

                                                    <button
                                                        onClick={() => handleEdit(campaign)}
                                                        className="rounded-lg p-2 text-amber-600 hover:bg-amber-100 transition"
                                                        title="Edit Campaign"
                                                    >
                                                        <Pencil size={18} />
                                                    </button>

                                                    <button
                                                        onClick={() =>
                                                            setDeleteId(campaign.camp_id)
                                                        }
                                                        className="rounded-lg p-2 text-red-600 hover:bg-red-100 transition"
                                                        title="Delete Campaign"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>

                                                </div>

                                            </td>

                                        </tr>

                                    ))

                                ) : (

                                    <tr>

                                        <td
                                            colSpan="5"
                                            className="py-12 text-center"
                                        >

                                            <Megaphone
                                                size={45}
                                                className="mx-auto mb-3 text-gray-300"
                                            />

                                            <h3 className="text-lg font-semibold text-gray-700">
                                                No Campaigns Found
                                            </h3>

                                            <p className="mt-2 text-sm text-gray-500">
                                                Create your first campaign to get started.
                                            </p>

                                        </td>

                                    </tr>

                                )}

                            </tbody>

                        </table>

                    </div>

                </div>

            </div>

            {deleteId && (
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1111]">
                    <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6 animate-fadeIn">

                        <h3 className="text-lg font-semibold mb-2">
                            Delete Campaign
                        </h3>

                        <p className="text-sm text-gray-600 mb-6">
                            Are you sure you want to delete this campaign?
                            This action cannot be undone.
                        </p>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setDeleteId(null)}
                                className="px-4 py-2 rounded-lg border hover:bg-gray-100"
                            >
                                Cancel
                            </button>

                            <button
                                onClick={handleDelete}
                                disabled={deleteLoading}
                                className="px-4 py-2 rounded-lg bg-red-600 text-white 
                               hover:bg-red-700 flex items-center gap-2 
                               disabled:opacity-70"
                            >
                                {deleteLoading ? (
                                    <>
                                        <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        Deleting...
                                    </>
                                ) : (
                                    "Delete"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
